import React, { useState, useCallback, useRef, useEffect } from 'react';
import { FestivalData, Artist, ImageData, POI, Show } from '../../../../model/FestivalData';
import { IconType } from 'react-icons';
import { FiGlobe, FiImage, FiMusic, FiPlus, FiUpload, FiYoutube } from 'react-icons/fi';
import PageHeader from '../../../../components/PageHeader';
import OverflowMenu from '../../../../components/OverflowMenu';
import SearchField from '../../../../components/SearchField';
import ImageEditor from '../ImageEditor/ImageEditor';
import { imageUrl } from '../../../../services/api';
import { generateCanonicalName, generateIdentifier } from '../../../../utils/identifiers';
import { formatDay, formatTime } from '../../../../utils/showTimes';
import Modal from '../../../../components/Modal';
import { useDialogs } from '../../../../components/Dialogs';
import { getImageUrl } from '../../../../services/imageService';
import { DataImportService } from '../../../../services/dataImportService';
import { useLanguage, useMessages } from '../../../../i18n/I18n';
import './ArtistsEditor.css';

interface ArtistsEditorProps {
  artists: FestivalData['artists'];
  onChange: (newArtists: FestivalData['artists']) => void;
  getApiImageUrl: (identifier: string) => string;
  /** Read only, to show where each artist plays */
  shows?: Show[];
  pois?: POI[];
}

interface MusicPreviewErrors {
  spotify_url: string | null;
  apple_music_url: string | null;
  youtube_url: string | null;
  soundcloud_url: string | null;
  other_url: string | null;
}

interface UrlValidationParams {
  domain: string;
  requiredPaths?: string[];
  requireParams?: string[];
  allowedHosts?: string[];
  examples: string[];
}

const VALIDATION_PARAMS: Record<keyof MusicPreviewErrors, UrlValidationParams> = {
  spotify_url: {
    domain: 'spotify',
    requiredPaths: ['/track/', '/album/', '/artist/'],
    allowedHosts: ['spotify.com'],
    examples: ['https://open.spotify.com/artist/...']
  },
  apple_music_url: {
    domain: 'apple',
    requiredPaths: ['/album/', '/artist/'],
    allowedHosts: ['music.apple.com'],
    examples: ['https://music.apple.com/artist/...']
  },
  youtube_url: {
    domain: 'youtube',
    requireParams: ['v'],
    allowedHosts: ['youtube.com', 'youtu.be'],
    examples: ['https://youtube.com/watch?v=...', 'https://youtu.be/...']
  },
  soundcloud_url: {
    domain: 'soundcloud',
    allowedHosts: ['soundcloud.com'],
    examples: ['https://soundcloud.com/...']
  },
  other_url: {
    domain: 'other',
    examples: ['https://example.com/...']
  }
};

// Labels and placeholders are in the artists messages (linkLabels, linkPlaceholders)
const MUSIC_LINK_FIELDS: { key: keyof MusicPreviewErrors; icon: IconType }[] = [
  { key: 'spotify_url', icon: FiMusic },
  { key: 'apple_music_url', icon: FiMusic },
  { key: 'youtube_url', icon: FiYoutube },
  { key: 'soundcloud_url', icon: FiMusic },
  { key: 'other_url', icon: FiGlobe },
];

const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

const NO_LINK_ERRORS: MusicPreviewErrors = {
  spotify_url: null,
  apple_music_url: null,
  youtube_url: null,
  soundcloud_url: null,
  other_url: null,
};

const ArtistsEditor: React.FC<ArtistsEditorProps> = ({ artists, onChange, getApiImageUrl, shows = [], pois = [] }) => {
  const t = useMessages('artists');
  const common = useMessages('common');
  const tImport = useMessages('dataImport');
  const { locale } = useLanguage();
  const sceneNames = new Map(pois.map(poi => [poi.identifier, poi.name]));
  const showsByArtist = new Map<string, Show[]>();
  [...shows]
    .sort((a, b) => new Date(a.start_date).getTime() - new Date(b.start_date).getTime())
    .forEach(show => showsByArtist.set(show.artist_id, [...(showsByArtist.get(show.artist_id) ?? []), show]));

  const describeShow = (show: Show) => {
    const start = new Date(show.start_date);
    return `${formatDay(start, locale)} · ${formatTime(start)} · ${sceneNames.get(show.scene_id) ?? t.unknownStage}`;
  };

  const [searchTerm, setSearchTerm] = useState('');
  const [editingArtist, setEditingArtist] = useState<Artist | null>(null);
  const [unsavedChanges, setUnsavedChanges] = useState<Partial<Artist> | null>(null);
  const [tempImages, setTempImages] = useState<Record<string, string>>({});
  const [musicPreviewErrors, setMusicPreviewErrors] = useState<MusicPreviewErrors>(NO_LINK_ERRORS);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showClearAllModal, setShowClearAllModal] = useState(false);
  const { confirm, alert } = useDialogs();

  const filteredArtists = artists.filter(artist => 
    artist.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const unsavedArtistId = "unsaved_artist_id"

  const handleAddArtist = () => {
    const newArtist: Artist = {
      identifier: unsavedArtistId,
      name: t.newArtistName,
      description: '',
      style: '',
      main_image: null,
      music_preview: {
        spotify_url: null,
        apple_music_url: null,
        youtube_url: null,
        soundcloud_url: null,
        other_url: null
      }
    };
    setEditingArtist(newArtist);
    setUnsavedChanges(null);
    setTempImages({});
    setMusicPreviewErrors(NO_LINK_ERRORS);
  };

  const handleEditArtist = (artist: Artist) => {
    setEditingArtist({...artist});
    setUnsavedChanges(null);
    setTempImages({});
    setMusicPreviewErrors(NO_LINK_ERRORS);
  };

  const handleImageChange = (newImage: ImageData | undefined) => {
    if (editingArtist) {
      setUnsavedChanges(prev => ({
        ...prev,
        main_image: newImage || null
      }));

      if (newImage && newImage.type === 'ApiImage' && 'file' in newImage && newImage.file) {
        setTempImages(prev => ({
          ...prev,
          [editingArtist.identifier]: URL.createObjectURL(newImage.file as Blob)
        }));
      } else {
        setTempImages(prev => {
          const { [editingArtist.identifier]: removed, ...rest } = prev;
          return rest;
        });
      }
    }
  };

  const handleSaveArtist = useCallback((artistToSave: Artist) => {
    const canonicalName = generateCanonicalName(artistToSave.name);
    
    const newIdentifier = artistToSave.identifier === unsavedArtistId
      ? generateIdentifier(canonicalName)
      : artistToSave.identifier;
    
    const updatedArtist: Artist = { 
      ...artistToSave,
      ...unsavedChanges,
      identifier: newIdentifier
    };
    
    const updatedArtists = artists.map(artist => 
      artist.identifier === artistToSave.identifier ? updatedArtist : artist 
    );

    if (!updatedArtists.some(artist => artist.identifier === artistToSave.identifier)) {
      updatedArtists.push(updatedArtist);
    }

    onChange(updatedArtists);
    setEditingArtist(null);
    setUnsavedChanges(null);
    setTempImages({});
  }, [artists, onChange, unsavedChanges]);

  const handleRemoveArtist = async (identifier: string) => {
    const artistToRemove = artists.find(artist => artist.identifier === identifier);
    if (artistToRemove && await confirm(t.deleteConfirm(artistToRemove.name), {
      title: t.deleteTitle,
      confirmLabel: common.delete,
      danger: true,
    })) {
      const updatedArtists = artists.filter(artist => artist.identifier !== identifier);
      onChange(updatedArtists);
      setEditingArtist(null);
    }
  };

  const getImageSrc = (artist: Artist): string | undefined => {
    if (tempImages[artist.identifier]) {
      return tempImages[artist.identifier];
    }
    return getImageUrl(artist.main_image, getApiImageUrl);
  };

  const validateUrl = (url: string, validationParams: UrlValidationParams): string | null => {
    if (!url.trim()) return null;
    
    const { domain, requiredPaths, requireParams, allowedHosts, examples } = validationParams;
    const addExample = (msg: string) => `${msg}\n${t.linkErrors.example(examples)}`;
    
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      return addExample(t.linkErrors.scheme);
    }

    try {
      const parsedUrl = new URL(url);

      if (allowedHosts && !allowedHosts.some(host => parsedUrl.hostname.includes(host))) {
        return addExample(t.linkErrors.format(domain));
      }

      if (domain === 'youtube' && parsedUrl.hostname === 'youtu.be') {
        return parsedUrl.pathname.length <= 1 ? addExample(t.linkErrors.youtubeId) : null;
      }

      if (requiredPaths && !requiredPaths.some(path => parsedUrl.pathname.includes(path))) {
        return addExample(t.linkErrors.paths(domain, requiredPaths.map(p => p.replace(/\//g, '')).join(', ')));
      }

      if (requireParams && !requireParams.every(param => parsedUrl.searchParams.has(param))) {
        return addExample(t.linkErrors.params(domain, requireParams.join(', ')));
      }

      return null;
    } catch {
      return addExample(t.linkErrors.invalid);
    }
  };

  const handleFileImport = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = e.target?.result as string;
        const data = DataImportService.parseJsonFile(content);
        
        if (!data.artists || !Array.isArray(data.artists)) {
          alert(t.invalidJson, { title: tImport.importFailed });
          return;
        }

        const validArtists: Artist[] = [];
        const errors: string[] = [];
        let refused = 0;

        data.artists.forEach((artist: any) => {
          const result = DataImportService.validateArtist(artist, artists);
          if (result.isValid) {
            validArtists.push(...result.data);
          } else {
            refused += 1;
            errors.push(...result.errors.map(error => `[${artist.name || tImport.unknown}] ${error}`));
          }
        });

        if (errors.length > 0) {
          console.warn('Validation errors:', errors);
        }

        if (validArtists.length === 0) {
          alert(t.noneValid, { title: tImport.importFailed });
          return;
        }

        onChange(validArtists);
        alert(t.importReport(validArtists.length, refused, errors), { title: tImport.importFinished });
      } catch (error) {
        console.error('Error parsing JSON:', error);
        alert(tImport.parseError, { title: tImport.importFailed });
      }
    };
    reader.readAsText(file);
  };

  const handleClearAllArtists = () => {
    onChange([]);
    setShowClearAllModal(false);
  };

  const handleLinkChange = (link: keyof MusicPreviewErrors, value: string) => {
    if (!editingArtist) return;
    const error = value.trim() ? validateUrl(value, VALIDATION_PARAMS[link]) : null;
    setMusicPreviewErrors(prev => ({ ...prev, [link]: value.trim() ? error : null }));
    setEditingArtist({ ...editingArtist, music_preview: { ...editingArtist.music_preview, [link]: value.trim() } });
  };

  // A refused link stays in the field (Save is disabled while it shows an error) instead of erasing the saved one
  const handleLinkBlur = (link: keyof MusicPreviewErrors, value: string) => {
    if (!editingArtist) return;
    const url = value.trim();
    const error = url ? validateUrl(url, VALIDATION_PARAMS[link]) : null;
    setMusicPreviewErrors(prev => ({ ...prev, [link]: url ? error : null }));
    setEditingArtist({ ...editingArtist, music_preview: { ...editingArtist.music_preview, [link]: url || null } });
  };

  const closeArtist = () => {
    setEditingArtist(null);
    setUnsavedChanges(null);
    setTempImages({});
  };

  const isNewArtist = editingArtist?.identifier === unsavedArtistId;

  return (
    <section className="artists-editor">
      <PageHeader title={t.title} count={artists.length}>
        <label className="btn import-button">
          <input
            type="file"
            accept=".json"
            onChange={handleFileImport}
            ref={fileInputRef}
          />
          <FiUpload aria-hidden="true" />
          {t.importJson}
        </label>
        <button type="button" className="btn btn-primary" onClick={handleAddArtist}>
          <FiPlus aria-hidden="true" />
          {t.add}
        </button>
        <OverflowMenu items={[{
          label: t.clearAll,
          danger: true,
          disabled: artists.length === 0,
          onSelect: () => setShowClearAllModal(true),
        }]} />
      </PageHeader>

      <div className="toolbar">
        <SearchField value={searchTerm} onChange={setSearchTerm} placeholder={t.search} />
      </div>

      <div className="data-table">
        <div className="data-table-scroll">
          <div className="data-table-head artists-columns" aria-hidden="true">
            <span>{t.artist}</span><span>{t.style}</span><span>{t.links}</span><span>{t.plays}</span>
          </div>
          {filteredArtists.map(artist => {
            const imageSrc = getImageSrc(artist);
            const artistShows = showsByArtist.get(artist.identifier) ?? [];
            return (
              <button
                type="button"
                key={artist.identifier}
                className="data-table-row artists-columns artist-row"
                onClick={() => handleEditArtist(artist)}
              >
                <span className="artist-cell">
                  {imageSrc
                    ? <img src={imageSrc} alt="" className="artist-photo" />
                    : <span className="artist-photo missing" aria-hidden="true"><FiImage /></span>}
                  <span className="artist-name">{artist.name}</span>
                </span>
                <span className="artist-style">{artist.style}</span>
                <span className="artist-links">
                  {MUSIC_LINK_FIELDS.filter(({ key }) => artist.music_preview?.[key]).map(({ key, icon: Icon }) => (
                    <Icon key={key} aria-label={t.linkLabels[key]} role="img" />
                  ))}
                </span>
                <span className="artist-plays">
                  {artistShows.length > 0 && describeShow(artistShows[0])}
                  {artistShows.length > 1 && <span className="artist-more-plays"> +{artistShows.length - 1}</span>}
                </span>
              </button>
            );
          })}
          {filteredArtists.length === 0 && (
            <div className="data-table-empty">
              {artists.length === 0 ? t.empty : t.noMatch}
            </div>
          )}
        </div>
      </div>

      <Modal isOpen={editingArtist !== null} onClose={closeArtist} variant="sheet">
        {editingArtist && (
          <>
            <h3>{isNewArtist ? t.add : t.edit}</h3>
            <ImageEditor
              currentImage={(unsavedChanges && 'main_image' in unsavedChanges
                ? unsavedChanges.main_image
                : editingArtist.main_image) || undefined}
              onChange={handleImageChange}
              getApiImageUrl={getApiImageUrl}
              tempPreviewUrl={tempImages[editingArtist.identifier]}
            />
            <div className="artist-form-row">
              <label className="artist-field">
                <span>{t.name}</span>
                <input
                  type="text"
                  value={editingArtist.name}
                  onChange={(e) => setEditingArtist({ ...editingArtist, name: e.target.value })}
                  placeholder={t.namePlaceholder}
                />
              </label>
              <label className="artist-field">
                <span>{t.style}</span>
                <input
                  type="text"
                  value={editingArtist.style}
                  onChange={(e) => setEditingArtist({ ...editingArtist, style: e.target.value })}
                  placeholder={t.stylePlaceholder}
                />
              </label>
            </div>
            <label className="artist-field">
              <span>{t.description}</span>
              <textarea
                value={editingArtist.description}
                onChange={(e) => setEditingArtist({ ...editingArtist, description: e.target.value })}
                placeholder={t.descriptionPlaceholder}
              />
            </label>
            <h4>{t.musicLinks}</h4>
            <div className="music-preview-section">
              {MUSIC_LINK_FIELDS.map(({ key }) => (
                <div className="input-group" key={key}>
                  <label htmlFor={`artist-link-${key}`}>{t.linkLabels[key]}</label>
                  <input
                    id={`artist-link-${key}`}
                    type="text"
                    value={editingArtist.music_preview[key] || ''}
                    onChange={(e) => handleLinkChange(key, e.target.value)}
                    onBlur={(e) => handleLinkBlur(key, e.target.value)}
                    placeholder={t.linkPlaceholders[key]}
                    className={musicPreviewErrors[key] ? 'error' : ''}
                  />
                  {musicPreviewErrors[key] && (
                    <div className="error-message">{musicPreviewErrors[key]}</div>
                  )}
                </div>
              ))}
            </div>
            {!isNewArtist && (showsByArtist.get(editingArtist.identifier) ?? []).length > 0 && (
              <>
                <h4>{t.plays}</h4>
                <ul className="artist-plays-list">
                  {(showsByArtist.get(editingArtist.identifier) ?? []).map(show => (
                    <li key={show.identifier}>{describeShow(show)}</li>
                  ))}
                </ul>
              </>
            )}
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => handleSaveArtist(editingArtist)}
                disabled={Object.values(musicPreviewErrors).some(error => error !== null)}
              >
                {common.save}
              </button>
              <button type="button" onClick={closeArtist}>{common.cancel}</button>
              {!isNewArtist && (
                <button type="button" className="danger" onClick={() => handleRemoveArtist(editingArtist.identifier)}>
                  {t.remove}
                </button>
              )}
            </div>
          </>
        )}
      </Modal>
      <Modal
        isOpen={showClearAllModal}
        onClose={() => setShowClearAllModal(false)}
      >
        <div className="modal-header">
          <h3>{t.clearAllTitle}</h3>
          <button className="modal-close" onClick={() => setShowClearAllModal(false)} aria-label={common.close}>×</button>
        </div>
        <p>{t.clearAllConfirm(artists.length)}</p>
        <div className="modal-actions">
          <button
            className="modal-delete"
            onClick={handleClearAllArtists}
          >
            {t.clearAllButton}
          </button>
          <button
            className="modal-cancel"
            onClick={() => setShowClearAllModal(false)}
          >
            {common.cancel}
          </button>
        </div>
      </Modal>
    </section>
  );
};

export default ArtistsEditor;
